import React, { cloneElement, forwardRef } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import withTheme from '../styles/withTheme'
import { mediaQueries } from '../../constants'
import Typography from '../Typography'
import useControlled from '../../utils/useControlled'

import './FormControlLabel.scss'
import { generateKey, stylesClasses } from '../../utils/classes'
import { responsiveStyles } from '../../utils/styles'
import { createUseStyles } from 'react-jss'

const CONTROL_PLACEMENTS = [
	'top-start',
	'top-end',
	'right',
	'right-start',
	'right-end',
	'bottom-start',
	'bottom-end',
	'left',
	'left-start',
	'left-end'
]

function generateControlPlacementStyles(mq) {
	return CONTROL_PLACEMENTS.reduce((styles, placement) => {
		const key = generateKey('Vb-form-control-label--control-', placement, mq)

		let controlStyles = {
			position: 'absolute',
			zIndex: 1
		}

		let labelStyles = {}

		switch (placement) {
			case 'top-start':
				controlStyles.top = 0
				controlStyles.left = 0
				break

			case 'top-end':
				controlStyles.top = 0
				controlStyles.right = 0
				break

			case 'bottom-start':
				controlStyles.bottom = 0
				controlStyles.left = 0
				break

			case 'bottom-end':
				controlStyles.bottom = 0
				controlStyles.right = 0
				break

			case 'left':
				controlStyles.left = 0
				controlStyles.top = '50%'
				controlStyles.transform = 'translateY(-50%)'
				labelStyles.paddingLeft = '3em'
				break

			case 'right':
				controlStyles.right = 0
				controlStyles.top = '50%'
				controlStyles.transform = 'translateY(-50%)'
				labelStyles.paddingRight = '3em'
				break
		}

		styles[key] = responsiveStyles(mq, {
			'&': {
				position: 'relative'
			},
			'& .Vb-form-control-label__input': controlStyles,
			'& .Vb-form-control-label__content': labelStyles
		})

		return styles
	}, {})
}

const styles = Object.keys(mediaQueries).reduce(
	(styles, mq) => ({
		...styles,
		...generateControlPlacementStyles(mq)
	}),
	{}
)

const useStyles = createUseStyles(styles, { name: 'Vb' })

const FormControlLabel = forwardRef(function FormControlLabel(props, ref) {
	const {
		checked: isCheckedProp,
		className,
		control,
		disabled: disabledProp,
		hiddenControl: hidden,
		inputRef,
		controlPlacement,
		labelDirection = 'row',
		label,
		name,
		onChange,
		size = 'large',
		value,
		variant = 'default',
		...other
	} = props

	let disabled = disabledProp
	const [checkedState, setCheckedState] = useControlled({
		controlled: isCheckedProp
	})

	if (typeof disabled === 'undefined' && typeof control.props.disabled !== 'undefined') {
		disabled = control.props.disabled
	}

	const handleCheckedChange = (newChecked) => {
		setCheckedState(newChecked)
	}

	const controlProps = ['checked', 'name', 'onChange', 'value', 'inputRef'].reduce(
		(controlPropsObj, propName) => {
			if (typeof control.props[propName] === 'undefined' && typeof props[propName] !== 'undefined') {
				return {
					...controlPropsObj,
					[propName]: props[propName]
				}
			}
			return controlPropsObj
		},
		{
			disabled,
			onCheckedChange: handleCheckedChange
		}
	)

	const classes = useStyles()

	return (
		<label
			ref={ref}
			className={clsx(
				'Vb-form-control-label',
				`Vb-form-control-label--${variant}`,
				`Vb-form-control-label--${size}`,
				checkedState && 'Vb-form-control-label--checked',
				hidden && 'Vb-form-control-label--control-hidden',
				controlPlacement && stylesClasses(controlPlacement, 'Vb-form-control-label--control-', classes),
				labelDirection === 'column' && 'Vb-form-control-label--label-direction-column',
				className
			)}
			{...other}
		>
			<span className="Vb-form-control-label__input">{cloneElement(control, controlProps)}</span>
			<Typography component="span" className={clsx('Vb-form-control-label__content', { disabled })}>
				{label}
			</Typography>
		</label>
	)
})

export default withTheme('Vb-form-control-label')(FormControlLabel)

FormControlLabel.defaultProps = {
	labelDirection: 'row',
	size: 'large',
	variant: 'default'
}

FormControlLabel.propTypes = {
	/**
	 * If `true`, the component appears selected.
	 */
	checked: PropTypes.bool,
	/**
	 * @ignore
	 */
	className: PropTypes.string,
	/**
	 * A control element. For instance, it can be a `Radio`, a `Switch` or a `Checkbox`.
	 */
	control: PropTypes.element.isRequired,
	/**
	 * The control placement.
	 */
	controlPlacement: (props, propName, componentName) => {
		const value = props[propName]
		if (typeof value === 'undefined') {
			return null
		} else if (typeof value === 'object') {
			let error = null

			const keys = Object.keys(value)

			keys.every((key) => {
				if (!mediaQueries[key]) {
					error = new Error(
						`${propName} object in ${componentName} has an invalid key. Accepted keys: ${Object.keys(
							mediaQueries
						).join(', ')}`
					)
					return false
				}
				if (!CONTROL_PLACEMENTS.includes(value[key])) {
					error = new Error(
						`${propName}.${key} in ${componentName} is not a valid value. Valid values: ${CONTROL_PLACEMENTS.join(
							', '
						)}`
					)
					return false
				}

				return true
			})

			if (error) {
				return error
			}
		} else {
			return CONTROL_PLACEMENTS.includes(value)
				? null
				: new Error(
					`${propName} in ${componentName} is not a valid value. Valid values: ${CONTROL_PLACEMENTS.join(
						', '
					)}`
				)
		}

		return null
	},
	/**
	 * If `true`, the control is disabled.
	 */
	disabled: PropTypes.bool,
	/**
	 * If `true`, the control is hidden.
	 */
	hiddenControl: PropTypes.bool,
	/**
	 * The text to be used in an enclosing label element.
	 */
	label: PropTypes.node,
	/**
	 * The flow direction of the label.
	 * @default 'row'
	 */
	labelDirection: PropTypes.oneOf(['row', 'column']),
	/**
	 * @ignore
	 */
	name: PropTypes.string,
	/**
	 * Callback fired when the state is changed.
	 *
	 * @param {object} event The event source of the callback.
	 * You can pull out the new checked state by accessing `event.target.checked` (boolean).
	 */
	onChange: PropTypes.func,
	/**
	 * The size to use.
	 * @default 'large'
	 */
	size: PropTypes.oneOf(['small', 'medium', 'large']),
	/**
	 * The value of the component.
	 */
	value: PropTypes.any,
	/**
	 * The variant to use.
	 * @default 'default'
	 */
	variant: PropTypes.oneOf(['default', 'block', 'item'])
}
